import type { ScrapeForumStatsResult } from './scrapeForumStats';
import type { ForumCountFields } from './forumStatChange';
import { formatLastUpdated, lastUpdatedSource } from './formatLastUpdated';

export type BulkScrapeRowStatus =
  | 'updated'
  | 'skipped'
  | 'unchanged'
  | 'failed'
  | 'no_selector';

export type StatFieldKey = 'topics' | 'posts' | 'members';

export type StatFieldChange = {
  field: StatFieldKey;
  label: string;
  oldValue: number;
  newValue: number | null;
  applied: boolean;
};

export type BulkScrapeForumMeta = {
  software?: string | null;
  updated_at?: string | null;
  created_at?: string | null;
};

export type BulkScrapeRowResult = {
  id: string;
  name: string;
  software: string | null;
  lastUpdatedLabel: string;
  status: BulkScrapeRowStatus;
  reason: string;
  changes: StatFieldChange[];
  warnings: string[];
  /** Önizlemede kayda uygulanacak alanlar (onay sonrası yazılır). */
  countUpdate?: Partial<ForumCountFields>;
  /** Sayılar değişmedi; onayda yalnızca son güncellenme tarihi yazılır. */
  touchUpdatedAt?: boolean;
};

function attachRowMeta(
  row: Omit<BulkScrapeRowResult, 'software' | 'lastUpdatedLabel'>,
  forum: BulkScrapeForumMeta
): BulkScrapeRowResult {
  return {
    ...row,
    software: forum.software?.trim() || null,
    lastUpdatedLabel: formatLastUpdated(lastUpdatedSource(forum)),
  };
}

export type BulkScrapeScope = 'active' | 'all';

export type BulkScrapeReportMode = 'preview' | 'committed';

export type BulkScrapeReport = {
  finishedAt: Date;
  total: number;
  scope: BulkScrapeScope;
  mode: BulkScrapeReportMode;
  rows: BulkScrapeRowResult[];
};

const FIELD_META: { key: StatFieldKey; label: string; countKey: keyof ForumCountFields; scrapedKey: keyof ScrapeForumStatsResult }[] = [
  { key: 'topics', label: 'Konu', countKey: 'topics_count', scrapedKey: 'topics_count' },
  { key: 'posts', label: 'İleti', countKey: 'posts_count', scrapedKey: 'posts_count' },
  { key: 'members', label: 'Üye', countKey: 'members_count', scrapedKey: 'members_count' },
];

export function formatStatNum(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString('tr-TR');
}

export function formatStatChangeCell(change: StatFieldChange): string {
  if (!change.applied && change.newValue === null) {
    return `${formatStatNum(change.oldValue)} → okunamadı`;
  }
  if (!change.applied) {
    return formatStatNum(change.oldValue);
  }
  if (change.newValue === change.oldValue) {
    return `${formatStatNum(change.oldValue)} (aynı)`;
  }
  return `${formatStatNum(change.oldValue)} → ${formatStatNum(change.newValue)}`;
}

export type SuspiciousChangeReason = 'large_delta' | 'large_ratio' | 'large_percent_increase';

export type SuspiciousStatChange = {
  suspicious: true;
  reasons: SuspiciousChangeReason[];
  delta: number;
  percentIncrease: number | null;
  ratio: number | null;
};

const SUSPICIOUS_MIN_ABSOLUTE_DELTA = 10_000;
const SUSPICIOUS_MIN_RATIO = 10;
const SUSPICIOUS_MIN_PERCENT_INCREASE = 0.1;

/** Büyük sıçrama / absürt artış (scrape hatası veya seçici yanlışlığı şüphesi). */
export function analyzeSuspiciousStatChange(change: StatFieldChange): SuspiciousStatChange | null {
  if (!change.applied || change.newValue === null || change.newValue === change.oldValue) {
    return null;
  }

  const oldValue = change.oldValue;
  const newValue = change.newValue;
  const delta = Math.abs(newValue - oldValue);
  const reasons: SuspiciousChangeReason[] = [];

  if (delta >= SUSPICIOUS_MIN_ABSOLUTE_DELTA) {
    reasons.push('large_delta');
  }

  let percentIncrease: number | null = null;
  let ratio: number | null = null;

  if (oldValue > 0 && newValue > 0) {
    ratio = newValue >= oldValue ? newValue / oldValue : oldValue / newValue;
    if (ratio >= SUSPICIOUS_MIN_RATIO) {
      reasons.push('large_ratio');
    }
    if (newValue > oldValue) {
      percentIncrease = (newValue - oldValue) / oldValue;
      if (percentIncrease > SUSPICIOUS_MIN_PERCENT_INCREASE) {
        reasons.push('large_percent_increase');
      }
    }
  } else if (oldValue === 0 && newValue >= SUSPICIOUS_MIN_ABSOLUTE_DELTA) {
    reasons.push('large_delta');
  }

  if (reasons.length === 0) return null;

  return {
    suspicious: true,
    reasons: [...new Set(reasons)],
    delta,
    percentIncrease,
    ratio,
  };
}

export function rowHasSuspiciousChanges(row: BulkScrapeRowResult): boolean {
  return row.changes.some((c) => analyzeSuspiciousStatChange(c) !== null);
}

/** İlk turda veri çekilemeyen forumlar 2. turda yeniden denenir. */
export function isBulkScrapeRetryEligible(row: BulkScrapeRowResult): boolean {
  return row.status === 'failed' || row.status === 'skipped';
}

export function countSuspiciousRows(rows: BulkScrapeRowResult[]): number {
  return rows.filter(rowHasSuspiciousChanges).length;
}

const SUSPICIOUS_REASON_LABELS: Record<SuspiciousChangeReason, string> = {
  large_delta: `±${SUSPICIOUS_MIN_ABSOLUTE_DELTA.toLocaleString('tr-TR')}+ mutlak değişim`,
  large_ratio: `${SUSPICIOUS_MIN_RATIO}× veya daha fazla sıçrama`,
  large_percent_increase: `%${(SUSPICIOUS_MIN_PERCENT_INCREASE * 100).toFixed(0)}+ artış`,
};

export function formatSuspiciousChangeHint(info: SuspiciousStatChange): string {
  return info.reasons.map((r) => SUSPICIOUS_REASON_LABELS[r]).join(' · ');
}

export function forumHasAnySelector(forum: {
  topics_selector?: string | null;
  posts_selector?: string | null;
  members_selector?: string | null;
}): boolean {
  return Boolean(
    forum.topics_selector?.trim() ||
      forum.posts_selector?.trim() ||
      forum.members_selector?.trim()
  );
}

function hasSelector(
  forum: { topics_selector?: string | null; posts_selector?: string | null; members_selector?: string | null },
  key: StatFieldKey
): boolean {
  if (key === 'topics') return Boolean(forum.topics_selector?.trim());
  if (key === 'posts') return Boolean(forum.posts_selector?.trim());
  return Boolean(forum.members_selector?.trim());
}

export function buildFieldChanges(
  forum: ForumCountFields & {
    topics_selector?: string | null;
    posts_selector?: string | null;
    members_selector?: string | null;
  },
  scraped: ScrapeForumStatsResult,
  countUpdate: Partial<ForumCountFields>
): StatFieldChange[] {
  return FIELD_META.map(({ key, label, countKey, scrapedKey }) => {
    const oldValue = (forum[countKey] as number) ?? 0;
    const scrapedVal = scraped[scrapedKey] as number | null | undefined;
    const newValue = scrapedVal ?? null;
    const applied = countKey in countUpdate;

    return { field: key, label, oldValue, newValue, applied };
  }).filter((c) => hasSelector(forum, c.field) || c.applied);
}

function skipReason(
  forum: { topics_selector?: string | null; posts_selector?: string | null; members_selector?: string | null },
  scraped: ScrapeForumStatsResult,
  changes: StatFieldChange[]
): string {
  const parts: string[] = [];
  const withSelector = changes.filter((c) => hasSelector(forum, c.field));

  if (withSelector.length === 0) {
    return 'Tanımlı seçici yok';
  }

  const unread = withSelector.filter((c) => c.newValue === null);
  if (unread.length === withSelector.length) {
    parts.push('Hiçbir seçiciden sayı okunamadı');
  } else if (unread.length > 0) {
    parts.push(
      `Okunamayan: ${unread.map((c) => c.label.toLowerCase()).join(', ')}`
    );
  }

  if (scraped.messages?.length) {
    parts.push(scraped.messages.join(' · '));
  }

  return parts.length > 0 ? parts.join(' — ') : 'Güncellenecek alan bulunamadı';
}

export function summarizeBulkScrapeReport(report: BulkScrapeReport) {
  const updated = report.rows.filter((r) => r.status === 'updated').length;
  const skipped = report.rows.filter((r) => r.status === 'skipped').length;
  const unchanged = report.rows.filter((r) => r.status === 'unchanged').length;
  const failed = report.rows.filter((r) => r.status === 'failed').length;
  const noSelector = report.rows.filter((r) => r.status === 'no_selector').length;
  const pendingSave = getPendingBulkSaveRows(report).length;
  return { updated, skipped, unchanged, failed, noSelector, pendingSave };
}

export function getPendingBulkSaveRows(report: BulkScrapeReport): BulkScrapeRowResult[] {
  return report.rows.filter((r) => {
    if (r.status === 'updated' && r.countUpdate && Object.keys(r.countUpdate).length > 0) {
      return true;
    }
    // Kontrol edildi, sayılar aynı — yine de son güncellenme tarihi yazılsın
    if (r.status === 'unchanged' && r.touchUpdatedAt) {
      return true;
    }
    return false;
  });
}

export function getBulkSaveSelectableRowIds(report: BulkScrapeReport): string[] {
  return getPendingBulkSaveRows(report).map((r) => r.id);
}

export function buildBulkScrapeRowResult(
  forum: ForumCountFields &
    BulkScrapeForumMeta & {
      id: string;
      name: string;
      topics_selector?: string | null;
      posts_selector?: string | null;
      members_selector?: string | null;
    },
  scraped: ScrapeForumStatsResult,
  countUpdate: Partial<ForumCountFields>,
  dbError?: string
): BulkScrapeRowResult {
  const changes = buildFieldChanges(forum, scraped, countUpdate);
  const warnings = scraped.messages ?? [];

  if (dbError) {
    return attachRowMeta(
      {
        id: forum.id,
        name: forum.name,
        status: 'failed',
        reason: dbError,
        changes,
        warnings,
      },
      forum
    );
  }

  if (Object.keys(countUpdate).length === 0) {
    return attachRowMeta(
      {
        id: forum.id,
        name: forum.name,
        status: 'skipped',
        reason: skipReason(forum, scraped, changes),
        changes,
        warnings,
      },
      forum
    );
  }

  const appliedChanges = changes.filter((c) => c.applied);
  const allSame = appliedChanges.every(
    (c) => c.newValue !== null && c.newValue === c.oldValue
  );

  if (allSame && appliedChanges.length > 0) {
    return attachRowMeta(
      {
        id: forum.id,
        name: forum.name,
        status: 'unchanged',
        reason: 'Okunan değerler aynı — onayda yalnızca son güncellenme tarihi yazılacak',
        changes,
        warnings,
        touchUpdatedAt: true,
      },
      forum
    );
  }

  return attachRowMeta(
    {
      id: forum.id,
      name: forum.name,
      status: 'updated',
      reason: warnings.length ? warnings.join(' · ') : 'Onay sonrası kaydedilecek',
      changes,
      warnings,
      countUpdate,
    },
    forum
  );
}

export function buildNoSelectorRow(
  forum: BulkScrapeForumMeta & {
    id: string;
    name: string;
    topics_count: number;
    posts_count: number;
    members_count: number;
    url?: string | null;
  }
): BulkScrapeRowResult {
  const urlNote = forum.url?.trim() ? '' : ' — URL de tanımlı değil';
  return attachRowMeta(
    {
      id: forum.id,
      name: forum.name,
      status: 'no_selector',
      reason: `Konu, ileti ve üye için CSS seçici tanımlı değil; toplu okuma yapılmadı${urlNote}`,
      changes: FIELD_META.map(({ key, label, countKey }) => ({
        field: key,
        label,
        oldValue: (forum[countKey] as number) ?? 0,
        newValue: null,
        applied: false,
      })),
      warnings: [],
    },
    forum
  );
}

export function buildFailedRow(
  forum: BulkScrapeForumMeta & {
    id: string;
    name: string;
    topics_count: number;
    posts_count: number;
    members_count: number;
  },
  errorMessage: string
): BulkScrapeRowResult {
  return attachRowMeta(
    {
      id: forum.id,
      name: forum.name,
      status: 'failed',
      reason: errorMessage,
      changes: FIELD_META.map(({ key, label, countKey }) => ({
        field: key,
        label,
        oldValue: (forum[countKey] as number) ?? 0,
        newValue: null,
        applied: false,
      })),
      warnings: [],
    },
    forum
  );
}
